import PolicyLayout, { PolicySection, P, UL, LI, Callout } from "./PolicyLayout";

const TOC = [
  { id: "summary", label: "The short version" },
  { id: "client-cancel", label: "Client cancellations" },
  { id: "no-show", label: "No-shows" },
  { id: "coach-cancel", label: "Coach cancellations" },
  { id: "reschedule", label: "Reschedules" },
  { id: "how-refunds-work", label: "How refunds are paid" },
  { id: "chargebacks", label: "Chargebacks" },
  { id: "exceptions", label: "Exceptions" },
  { id: "contact", label: "Contact" },
];

export default function RefundPolicy() {
  return (
    <PolicyLayout
      eyebrow="Refunds"
      title="Refund & Cancellation Policy"
      updated="February 2026"
      intro="We want cancellations to be fair to both sides. Here's exactly how they work on Coachly."
      toc={TOC}
      testId="policy-refund"
    >
      <PolicySection id="summary" title="1. The short version">
        <UL>
          <LI><strong>Free client cancellation up to 24 hours</strong> before the session — full refund.</LI>
          <LI><strong>Within 24 hours</strong> — a 50% cancellation fee applies, 50% is refunded.</LI>
          <LI><strong>No-shows</strong> — the session fee is not refunded.</LI>
          <LI><strong>Coach-initiated cancellation</strong> — always 100% refund to the client.</LI>
          <LI>Refunds land back on the original card in <strong>5–10 business days</strong> via Stripe.</LI>
        </UL>
        <Callout>
          A coach may set their own stricter or looser policy on their profile page. If a
          coach's posted policy conflicts with this document, the coach's policy wins for
          bookings made through that coach's profile.
        </Callout>
      </PolicySection>

      <PolicySection id="client-cancel" title="2. Client cancellations">
        <P>
          You can cancel any confirmed booking from your account. The refund amount
          depends on how far ahead of the session start you cancel:
        </P>
        <div className="mt-4 rounded-xl border border-slate-200 overflow-hidden text-[14px]">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-500 text-[12px] uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left py-3 px-4 font-semibold">When you cancel</th>
                <th className="text-left py-3 px-4 font-semibold">You are refunded</th>
                <th className="text-left py-3 px-4 font-semibold">Coach receives</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-800">
              <tr>
                <td className="py-3 px-4">24+ hours before start</td>
                <td className="py-3 px-4 font-semibold text-[#059669]">100%</td>
                <td className="py-3 px-4">$0</td>
              </tr>
              <tr>
                <td className="py-3 px-4">Less than 24 hours before start</td>
                <td className="py-3 px-4 font-semibold text-amber-700">50%</td>
                <td className="py-3 px-4">50% (minus Coachly fee)</td>
              </tr>
              <tr>
                <td className="py-3 px-4">After the session was scheduled to start</td>
                <td className="py-3 px-4 font-semibold text-slate-500">0%</td>
                <td className="py-3 px-4">Full payout (minus Coachly fee)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </PolicySection>

      <PolicySection id="no-show" title="3. No-shows">
        <P>
          If a client doesn't join an online session within the first 15 minutes, or
          doesn't arrive at the agreed in-person location within the first 15 minutes,
          the coach may mark the session as a no-show. No-show sessions are not refunded
          — the coach reserved that time for you.
        </P>
      </PolicySection>

      <PolicySection id="coach-cancel" title="4. Coach cancellations">
        <P>
          If a coach cancels for any reason, the client is refunded 100% automatically.
          Repeated last-minute coach cancellations may result in profile suspension per
          our <a href="/coach-guidelines" className="text-[#2563EB] hover:underline">Coach Guidelines</a>.
        </P>
      </PolicySection>

      <PolicySection id="reschedule" title="5. Reschedules">
        <P>
          Reschedules requested at least 24 hours before the session are free for both
          sides. Rescheduling within 24 hours counts as a cancellation followed by a new
          booking — the standard fee schedule above applies.
        </P>
      </PolicySection>

      <PolicySection id="how-refunds-work" title="6. How refunds are paid">
        <UL>
          <LI>Refunds go back to the original payment method via Stripe.</LI>
          <LI>Bank card refunds usually settle in 5–10 business days. Some banks are faster; a few take longer.</LI>
          <LI>The Coachly platform fee (10%) is refunded together with the session fee on eligible cancellations. If the coach's share was already paid out, we recover it from their next payout.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="chargebacks" title="7. Chargebacks">
        <P>
          Please write to <a href="mailto:support@coachly.app" className="text-[#2563EB] hover:underline">support@coachly.app</a>{" "}
          before initiating a chargeback with your card provider. In most cases we can
          resolve the issue directly and much faster. Fraudulent chargebacks may result
          in account suspension.
        </P>
      </PolicySection>

      <PolicySection id="exceptions" title="8. Exceptions">
        <P>We look at each of these on a case-by-case basis and lean toward refunding:</P>
        <UL>
          <LI>Documented medical emergency for the client.</LI>
          <LI>Bereavement or family emergency.</LI>
          <LI>Technical failure clearly caused by Coachly that made the session impossible.</LI>
          <LI>Local emergencies (natural disaster, official public-safety warning).</LI>
        </UL>
      </PolicySection>

      <PolicySection id="contact" title="9. Contact">
        <P>
          Refund questions? Write to{" "}
          <a href="mailto:support@coachly.app" className="text-[#2563EB] hover:underline">support@coachly.app</a>{" "}
          with your booking reference and we'll respond within one business day.
        </P>
      </PolicySection>
    </PolicyLayout>
  );
}
